import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit, Trash2, Filter, Search, Users } from 'lucide-react';
import { shiftGroupApi } from '../services/api';
import { Location, ShiftGroup, CreateShiftGroupRequest } from '../types';
import BasketSelect from './BasketSelect';

interface ShiftGroupsSettingsPanelProps {
  locations: Location[];
}

const itemsPerPage = 10;

export default function ShiftGroupsSettingsPanel({ locations }: ShiftGroupsSettingsPanelProps) {
  const [groups, setGroups] = useState<ShiftGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');
  const [locationFilter, setLocationFilter] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ShiftGroup | null>(null);
  const [form, setForm] = useState<CreateShiftGroupRequest & { isActive?: boolean }>({
    name: '',
    description: '',
    locationIds: [],
    isActive: true,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await shiftGroupApi.getAll({ includeInactive: true });
      setGroups(data || []);
    } catch (error) {
      console.error('Error loading shift groups:', error);
      alert('Failed to load shift groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    return groups.filter((g) => {
      const matchesFilter =
        filter === 'all' ||
        (filter === 'active' && g.isActive) ||
        (filter === 'inactive' && !g.isActive);
      if (!matchesFilter) return false;

      if (locationFilter.length > 0) {
        const ids = g.locationIds || [];
        if (!locationFilter.some((id) => ids.includes(id))) return false;
      }

      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${g.name} ${g.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [groups, filter, locationFilter, search]);

  const counts = useMemo(
    () => ({
      total: groups.length,
      active: groups.filter((g) => g.isActive).length,
      inactive: groups.filter((g) => !g.isActive).length,
      filtered: filtered.length,
    }),
    [groups, filtered]
  );

  const handleAdd = () => {
    setEditing(null);
    setForm({ name: '', description: '', locationIds: [], isActive: true });
    setShowModal(true);
  };

  const handleEdit = (group: ShiftGroup) => {
    setEditing(group);
    setForm({
      name: group.name,
      description: group.description || '',
      locationIds: group.locationIds || [],
      isActive: group.isActive,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      alert('Please enter a group name.');
      return;
    }
    if (!form.locationIds || form.locationIds.length === 0) {
      alert('Please select at least one location.');
      return;
    }
    try {
      if (editing) {
        await shiftGroupApi.update(editing.id, {
          name: form.name.trim(),
          description: form.description || '',
          locationIds: form.locationIds,
          isActive: form.isActive,
        });
      } else {
        await shiftGroupApi.create({
          name: form.name.trim(),
          description: form.description || '',
          locationIds: form.locationIds,
        });
      }
      setShowModal(false);
      await refresh();
    } catch (error: any) {
      console.error('Error saving shift group:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to save shift group';
      alert(typeof message === 'string' ? message : 'Failed to save shift group');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this shift group?')) return;
    try {
      await shiftGroupApi.delete(id);
      await refresh();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'Failed to delete shift group. It may be assigned to one or more shifts.';
      alert(message);
    }
  };

  const locationName = (id: number) => locations.find((l) => l.id === id)?.name || `#${id}`;

  const sorted = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  const totalPages = Math.max(1, Math.ceil(sorted.length / itemsPerPage));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) {
    return <div className="py-12 text-center text-neutral-600">Loading shift groups...</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Shift Groups</h2>
          <p className="text-sm md:text-base text-neutral-600">
            Groups used on shifts for &quot;Working with&quot; on My Schedules ({counts.filtered} of{' '}
            {counts.total} showing)
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-neutral-600" />
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
              className="w-32 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active ({counts.active})</option>
              <option value="inactive">Inactive ({counts.inactive})</option>
              <option value="all">All ({counts.total})</option>
            </select>
          </div>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Shift Group
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search shift groups..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="mb-4">
        <BasketSelect
          label="Filter by Location"
          availableItems={locations
            .filter((loc) => loc.isActive)
            .map((loc) => ({ id: loc.id, label: loc.name }))}
          selectedIds={locationFilter}
          onChange={(ids) => {
            setLocationFilter(ids.map((id) => (typeof id === 'string' ? parseInt(id, 10) : id)));
            setPage(1);
          }}
          placeholder="Select locations to filter groups..."
        />
      </div>

      {pageItems.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
          <Users className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 mb-2">No shift groups found</h3>
          <p className="text-neutral-600 mb-4">Create groups to link colleagues on overlapping shifts.</p>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Shift Group
          </button>
        </div>
      ) : (
        <>
          <div className="hidden md:block">
            <div className="border border-neutral-200 rounded-lg bg-white overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-50">
                  <tr className="border-b border-neutral-200">
                    <th className="text-left p-4 font-medium text-neutral-700">Name</th>
                    <th className="text-left p-4 font-medium text-neutral-700">Locations</th>
                    <th className="text-left p-4 font-medium text-neutral-700">Status</th>
                    <th className="text-right p-4 font-medium text-neutral-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((group) => (
                    <tr key={group.id} className="border-b border-neutral-100">
                      <td className="p-4">
                        <div className="font-medium text-neutral-900">{group.name}</div>
                        {group.description && (
                          <div className="text-sm text-neutral-500">{group.description}</div>
                        )}
                      </td>
                      <td className="p-4 text-sm text-neutral-700">
                        {(group.locationIds || []).map(locationName).join(', ') || '—'}
                      </td>
                      <td className="p-4">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            group.isActive
                              ? 'bg-green-50 text-green-700'
                              : 'bg-neutral-100 text-neutral-600'
                          }`}
                        >
                          {group.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => handleEdit(group)}
                          className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(group.id)}
                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden space-y-3">
            {pageItems.map((group) => (
              <div key={group.id} className="bg-white border border-neutral-200 rounded-lg p-4">
                <div className="flex justify-between gap-2">
                  <div>
                    <div className="font-medium">{group.name}</div>
                    {group.description && (
                      <p className="text-sm text-neutral-600 mt-1">{group.description}</p>
                    )}
                    <p className="text-xs text-neutral-500 mt-2">
                      {(group.locationIds || []).map(locationName).join(', ') || 'No locations'}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(group)}
                      className="p-2 text-neutral-600 hover:text-blue-600 rounded-md"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(group.id)}
                      className="p-2 text-neutral-600 hover:text-red-600 rounded-md"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 text-sm">
              <span className="text-neutral-600">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1 border rounded disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1 border rounded disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold mb-2">
              {editing ? 'Edit Shift Group' : 'Add Shift Group'}
            </h3>
            <p className="text-sm text-neutral-600 mb-6">
              Employees on overlapping shifts in the same group see each other under Working with.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ward A Day"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                />
              </div>
              <BasketSelect
                label="Locations"
                availableItems={locations
                  .filter((loc) => loc.isActive)
                  .map((loc) => ({ id: loc.id, label: loc.name }))}
                selectedIds={(form.locationIds || []).map((id) => Number(id))}
                onChange={(ids) =>
                  setForm({
                    ...form,
                    locationIds: ids.map((id) => Number(id)),
                  })
                }
                required={true}
                placeholder="Select at least one location..."
              />
              {editing && (
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    checked={form.isActive !== false}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active
                </label>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {editing ? 'Update Group' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
